// Client Groups — Table Management screen for client_groups / client_group_members (step 8 part 3). A group (e.g.
// "Hertz Group" = Hertz + DTAG) is picked on any report like a client. Built like NoticeTypesTable.
//
// ⛔ EDITING IS DATABASE-ENFORCED: RLS lets only staff with ntrg_staff.manages_doc_types write; others read. Every write
// checks the affected-row count, because a write the policy blocks returns 0 rows and NO error.
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { loadClientGroups } from '../utils/clientGroups';

export default function ClientGroupsTable({ c, s }) {
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);           // { tone:'error'|'info', text }
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    const [g, { data: cl, error: clErr }] = await Promise.all([loadClientGroups(), supabase.from('clients').select('id, name').order('name')]);
    if (!g.ok) setMsg({ tone: 'error', text: g.error });
    else if (clErr) setMsg({ tone: 'error', text: `Clients could not be loaded: ${clErr.message}` });
    setGroups(g.ok ? g.data : []);
    setClients(cl || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase.rpc('manages_doc_types').then(({ data, error }) => setCanEdit(!error && data === true), () => setCanEdit(false));
  }, [load]);

  const friendly = (e) => (e?.code === '23505' ? 'that name or client is already there' : e?.message || '0 rows changed (no permission?)');
  // Every write: affected-row check, then reload.
  async function write(q, what) {
    setBusy(true); setMsg(null);
    const { data, error } = await q;
    setBusy(false);
    if (error || !data?.length) { setMsg({ tone: 'error', text: `${what} — not saved: ${friendly(error)}` }); return false; }
    await load();
    return true;
  }

  const addGroup = async () => {
    const name = draft.trim();
    if (!name) return;
    if (await write(supabase.from('client_groups').insert({ name, sort: Math.max(0, ...groups.map((g) => g.sort)) + 10 }).select('id'), `Add "${name}"`)) setDraft('');
  };
  const rename = (g) => {
    const name = window.prompt('Rename this group', g.name);
    if (name && name.trim() && name.trim() !== g.name) write(supabase.from('client_groups').update({ name: name.trim(), updated_at: new Date().toISOString() }).eq('id', g.id).select('id'), `Rename "${g.name}"`);
  };
  const retire = (g) => write(supabase.from('client_groups').update({ active: !g.active, updated_at: new Date().toISOString() }).eq('id', g.id).select('id'), `${g.active ? 'Retire' : 'Restore'} "${g.name}"`);
  const remove = (g) => {
    if (window.confirm(`Delete the group "${g.name}"? Its clients are not touched. Saved reports that use it lose the group.`)) {
      write(supabase.from('client_groups').delete().eq('id', g.id).select('id'), `Delete "${g.name}"`);
    }
  };
  const addMember = (g, clientId) => write(supabase.from('client_group_members').insert({ group_id: g.id, client_id: clientId }).select('client_id'), 'Add the client');
  const removeMember = (g, clientId) => write(supabase.from('client_group_members').delete().eq('group_id', g.id).eq('client_id', clientId).select('client_id'), 'Remove the client');

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;
  const nameOf = (id) => clients.find((x) => x.id === id)?.name || id;
  const small = { ...s.btnGhost, padding: '3px 8px', fontSize: '11px' };
  const input = { padding: '5px 8px', fontSize: '12px', border: `1px solid ${c.inputBorder}`, borderRadius: '6px', background: c.input, color: c.text };

  return (
    <div style={s.card}>
      <div style={s.cardH}>
        <div>
          <div style={s.cardT}>Client Groups</div>
          <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>
            Groups of clients a report can be run for together. A saved report keeps the group, so a client added here is in its next run.
            {canEdit ? '' : ' Read-only — only staff with document-type admin rights can edit.'}
          </div>
        </div>
      </div>
      {msg && <div style={{ padding: '10px 18px', fontSize: '12px', fontWeight: '600', color: msg.tone === 'error' ? 'var(--red)' : '#34d399', borderBottom: `1px solid ${c.border}` }}>{msg.text}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={s.th}>Group</th><th style={s.th}>Clients</th><th style={s.th} /></tr></thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.id} style={{ opacity: g.active ? 1 : 0.55 }}>
              <td style={{ ...s.td, fontWeight: 600 }}>
                {g.name}
                {!g.active && <span style={{ ...s.badge('248,113,113'), marginLeft: '6px' }}>Retired</span>}
              </td>
              <td style={s.td}>
                <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {g.clientIds.length === 0 && <span style={{ color: c.muted, fontSize: '12px' }}>No clients yet</span>}
                  {g.clientIds.map((id) => (
                    <span key={id} style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '8px', border: `1px solid ${c.border}` }}>
                      {nameOf(id)}
                      {canEdit && <button type="button" title={`Remove ${nameOf(id)}`} disabled={busy} onClick={() => removeMember(g, id)}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', marginLeft: '4px', padding: 0 }}>×</button>}
                    </span>
                  ))}
                  {canEdit && (
                    <select value="" disabled={busy} onChange={(e) => { if (e.target.value) addMember(g, e.target.value); }} style={{ ...input, padding: '2px 6px' }}>
                      <option value="">+ client</option>
                      {clients.filter((x) => !g.clientIds.includes(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  )}
                </span>
              </td>
              <td style={{ ...s.td, whiteSpace: 'nowrap', textAlign: 'right' }}>{canEdit && <>
                <button style={small} disabled={busy} onClick={() => rename(g)}>Rename</button>
                <button style={{ ...small, marginLeft: '4px' }} disabled={busy} onClick={() => retire(g)}>{g.active ? 'Retire' : 'Restore'}</button>
                <button style={{ ...small, marginLeft: '4px', color: '#f87171' }} disabled={busy} onClick={() => remove(g)}>Delete</button>
              </>}</td>
            </tr>
          ))}
          {canEdit && (
            <tr><td colSpan="3" style={{ ...s.td, borderBottom: 'none' }}>
              <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                <input placeholder="New group name" value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addGroup(); }} style={input} />
                <button style={small} disabled={busy || !draft.trim()} onClick={addGroup}>+ Add Group</button>
              </span>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
